import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listFotoReview,
  listFotoReviewProjectleiders,
  type FotoReviewProjectleider,
} from '@/api/tasks';

interface ReviewProject {
  projectId: number;
  projectName: string;
  collectorsToReview: number;
  meerwerkenToReview?: number;
  latestOpDruk: string | null;
  /** Latest of collector op-druk ∪ pending-meerwerk date — populated for meerwerk-only rows too. */
  latestActivity?: string | null;
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function FotoReviewView() {
  const [projects, setProjects] = useState<ReviewProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectleiders, setProjectleiders] = useState<FotoReviewProjectleider[]>([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Selection lives in the URL (?projectleider=) so it survives refresh/back —
  // same convention as ?task=. '' = mijn projecten, 'all' = alle, '<id>' = a PL.
  const selection = searchParams.get('projectleider') ?? '';

  // Guards against a slow stale response: if the dropdown changes while a fetch
  // is in flight, the earlier (possibly slower) response must not overwrite the
  // newer selection's queue. Same pattern as FotoReviewProjectView.
  const activeSelectionRef = useRef(selection);

  useEffect(() => {
    listFotoReviewProjectleiders()
      .then(setProjectleiders)
      .catch(() => setProjectleiders([])); // dropdown still shows Mijn/Alle
  }, []);

  useEffect(() => {
    activeSelectionRef.current = selection;
    const requested = selection;
    const isStale = () => activeSelectionRef.current !== requested;
    setLoading(true);
    setError(null);
    listFotoReview<{ projects: ReviewProject[] }>(selection || undefined)
      .then(data => { if (!isStale()) setProjects(data.projects); })
      .catch(err => { if (!isStale()) setError(err.message || 'Fout bij laden'); })
      .finally(() => { if (!isStale()) setLoading(false); });
  }, [selection]);

  const handleSelect = (value: string) => {
    setSearchParams(value ? { projectleider: value } : {}, { replace: true });
  };

  const totalCollectors = projects.reduce((sum, p) => sum + p.collectorsToReview, 0);
  const totalMeerwerken = projects.reduce((sum, p) => sum + (p.meerwerkenToReview ?? 0), 0);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Foto Controle</h1>
        <p className="text-sm text-gray-500 mt-1">
          Projecten met collectoren waarvan de foto's nog goedgekeurd moeten worden
        </p>
        <div className="mt-3">
          <label htmlFor="pl-select" className="sr-only">Projectleider</label>
          <select
            id="pl-select"
            value={selection}
            onChange={e => handleSelect(e.target.value)}
            className="w-full sm:w-auto bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-td-blue focus:ring-1 focus:ring-td-blue"
          >
            <option value="">Mijn projecten</option>
            <option value="all">Alle projectleiders</option>
            {projectleiders.map(pl => (
              <option key={pl.id} value={String(pl.id)}>{pl.name}</option>
            ))}
            {/* Roster fetch failed but URL still carries a PL id — keep the
                select from rendering blank. */}
            {selection && selection !== 'all' && !projectleiders.some(pl => String(pl.id) === selection) && (
              <option value={selection}>Projectleider {selection}</option>
            )}
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-medium">Alles goedgekeurd</p>
          <p className="text-sm mt-1">Er zijn geen collectoren of meerwerken die wachten op goedkeuring</p>
        </div>
      )}

      {!loading && !error && projects.length > 0 && (
        <>
          <div className="mb-4 text-sm text-gray-500">
            {projects.length} {projects.length === 1 ? 'project' : 'projecten'}
            {totalCollectors > 0 && (
              <> &middot; {totalCollectors} {totalCollectors === 1 ? 'collector' : 'collectoren'}</>
            )}
            {totalMeerwerken > 0 && (
              <> &middot; {totalMeerwerken} {totalMeerwerken === 1 ? 'meerwerk' : 'meerwerken'}</>
            )}
          </div>

          <div className="space-y-2">
            {projects.map(p => (
              <button
                key={p.projectId}
                type="button"
                onClick={() => navigate(`/foto-controle/${p.projectId}${selection ? `?projectleider=${encodeURIComponent(selection)}` : ''}`)}
                className="w-full text-left bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-td-blue/40 hover:shadow-sm transition flex items-center gap-4 group"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate group-hover:text-td-blue transition">
                    {p.projectName}
                  </p>
                  {p.latestOpDruk ? (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Laatste op druk: {formatDate(p.latestOpDruk)}
                    </p>
                  ) : p.latestActivity ? (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Laatste activiteit: {formatDate(p.latestActivity)}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {p.collectorsToReview > 0 && (
                    <span className="bg-amber-100 text-amber-700 text-sm font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {p.collectorsToReview} {p.collectorsToReview === 1 ? 'collector' : 'collectoren'}
                    </span>
                  )}
                  {(p.meerwerkenToReview ?? 0) > 0 && (
                    <span className="bg-purple-100 text-purple-700 text-sm font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {p.meerwerkenToReview} {p.meerwerkenToReview === 1 ? 'meerwerk' : 'meerwerken'}
                    </span>
                  )}
                  <svg className="w-5 h-5 text-gray-300 group-hover:text-td-blue transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
